import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, interval, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from '@auth0/auth0-angular';

@Injectable({
  providedIn: 'root'
})
export class LiveDataService implements OnDestroy {
  private webSocket: WebSocket | null = null;
  private destroy$ = new Subject<void>();
  private heartbeatSubscription: Subscription | null = null;
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string>('');
  private messageSubject = new BehaviorSubject<any>(null);

  // Expose observables for components to subscribe to
  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public error$ = this.errorSubject.asObservable();
  public message$ = this.messageSubject.asObservable();

  constructor(private auth: AuthService) {}

  /**
   * Initialize the WebSocket connection for a specific hub
   * @param hubId The ID of the hub to connect to
   */
  public connectToHub(hubId: string): void {
    // Close any existing connection
    this.disconnect();

    // Reset error
    this.errorSubject.next('');

    // Get the access token from Auth0
    this.auth.getAccessTokenSilently().subscribe({
      next: (token) => {
        this.initializeWebSocket(token, hubId);
      },
      error: (err) => {
        const errorMessage = err && typeof err === 'object' && 'message' in err
          ? err.message
          : 'Unknown error';
        this.errorSubject.next(`Error getting access token: ${errorMessage}`);
        console.error('Error getting access token:', err);
      }
    });
  }

  /**
   * Disconnect the WebSocket connection
   */
  public disconnect(): void {
    console.log('WebSocket disconnecting.');
    if (this.webSocket) {
      this.webSocket.close();
      this.webSocket = null;
    }

    // Stop the heartbeat
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = null;
    }

    // Update connection status
    this.connectionStatusSubject.next(false);
  }

  /**
   * Clean up resources when the service is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }

  /**
   * Initialize the WebSocket connection
   * @param token The access token for authorization
   * @param hubId The ID of the hub to connect to
   */
  private initializeWebSocket(token: string, hubId: string): void {
    try {
      // Create a new WebSocket connection
      this.webSocket = new WebSocket(environment.liveDataServiceUrl);

      // Set up event handlers
      this.webSocket.onopen = () => {
        console.log('WebSocket connection opened');
        this.connectionStatusSubject.next(true);

        // Send authorization message
        this.sendAuthorizationMessage(token);

        // Send subscription message
        this.sendSubscriptionMessage(hubId);

        // Start heartbeat
        this.startHeartbeat();
      };

      this.webSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          this.messageSubject.next(data);
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      this.webSocket.onerror = (event) => {
        console.error('WebSocket error:', event);
        this.errorSubject.next('WebSocket connection error');
        this.connectionStatusSubject.next(false);
      };

      this.webSocket.onclose = () => {
        console.log('WebSocket connection closed');
        this.connectionStatusSubject.next(false);

        // Stop the heartbeat
        if (this.heartbeatSubscription) {
          this.heartbeatSubscription.unsubscribe();
          this.heartbeatSubscription = null;
        }
      };
    } catch (err) {
      console.error('Error initializing WebSocket:', err);
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? err.message
        : 'Unknown error';
      this.errorSubject.next(`Error initializing WebSocket: ${errorMessage}`);
    }
  }

  /**
   * Send an authorization message to the WebSocket
   * @param token The access token for authorization
   */
  private sendAuthorizationMessage(token: string): void {
    if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message = {
      type: 'authorize',
      payload:{ access_token: token}
    };

    this.webSocket.send(JSON.stringify(message));
    console.log('Authorization message sent');
  }

  /**
   * Send a subscription message to the WebSocket
   * @param hubId The ID of the hub to subscribe to
   */
  private sendSubscriptionMessage(hubId: string): void {
    if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message = {
      type: 'subscribe',
      payload: {
        type: 'device',
        id: hubId
      }
    };

    this.webSocket.send(JSON.stringify(message));
    console.log(`Subscription message sent for hub ${hubId}`);
  }

  /**
   * Start sending heartbeat messages every 10 seconds
   */
  private startHeartbeat(): void {
    // Stop any existing heartbeat
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
    }

    // Start a new heartbeat
    this.heartbeatSubscription = interval(10000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.sendHeartbeatMessage();
      });
  }

  /**
   * Send a heartbeat message to the WebSocket
   */
  private sendHeartbeatMessage(): void {
    if (!this.webSocket || this.webSocket.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message = {
      type: 'heartbeat',
      payload: ''
    };

    this.webSocket.send(JSON.stringify(message));
    console.log('Heartbeat message sent');
  }
}
